import { Component, OnInit, OnChanges, SimpleChanges, Input, Output, EventEmitter } from '@angular/core';
import { DataService } from '../../services/data.service';
import { StoreService } from '../../services/store.service';
import { ToolsService } from '../../services/tools.service';
import { ActivatedRoute } from '@angular/router';
import { CustomRegisterCategory, Product, ProductModifier, ShoppingCartProduct, ModifierDetail, ShoppingCart } from '../../models/models';
import { IHttpPostResponse } from '../../interfaces/http-post-response';
import { pages } from '../../alv-enums';
import * as tools from '../../tools';
import { TimeSlots } from 'src/app/models/time-slots';
import { SessionProduct, LineItem } from 'src/app/models/session-product';
import { Utility } from 'src/app/common/utility';
import moment from 'moment';
import { ReCaptchaV3Service } from 'ng-recaptcha';


@Component({
	selector: 'order',
	templateUrl: './order.component.html',
	styleUrls: ['./order.component.scss']
})
export class OrderComponent implements OnInit, OnChanges {
	@Input() private currentPage: pages;
	@Input() private registerId: number;

	@Output() private pageChange = new EventEmitter<pages>();

	public registerCategories: CustomRegisterCategory[];
	public products: Product[];
	public selectedSessionProductDate: Date = Utility.getDateWithtimeZoneOffset(new Date())
	public productWithModifiers: Product;
	public sessionsProduct: Product;
	public sessionsProductDTO: SessionProduct;
	public productModifiers: ProductModifier[];
	public timeSlots: TimeSlots[];
	public selectedTimeslot: TimeSlots;
	public subtotal: number;
	public discount: number;
	public tax: number;
	public openCart = false;

	public openModifier = false;
	public modifier = false;
	public sessions = false;
	public qtyModifierSessions = 1;
	public recaptcha_Key;
	public recpatcha_Key2;

	/** This is here to allow the html code to access the pages enum. */
	public pagesEnum: any;

	constructor(private dataService: DataService
		, public storeService: StoreService
		, private activatedRoute: ActivatedRoute
		, private toolsService: ToolsService
		, private recaptchaV3Service: ReCaptchaV3Service
	) {
		this.pagesEnum = pages;
	}

	ngOnChanges(changes: SimpleChanges) {
		const self = this;
		
		// Reset transaction fees when returning to the order page
		if (changes.currentPage && changes.currentPage.currentValue === pages.Order) {
			self.resetTransactionFees();
		}
	}

	ngOnInit() {
		const self = this;

		this.activatedRoute.paramMap.subscribe(params => {
			self.registerId = +params.get('registerId');
		});

		self.subtotal = 0;
		self.discount = 0;
		self.tax = 0;
		self.recaptcha_Key = this.storeService.kioskSettings.recaptcha_Key;
		self.recpatcha_Key2 = this.storeService.kioskSettings.recaptcha_Keyv2;
		self.getRegisterCategories();

		self.products = [];
		
		// Reset transaction fees on initial load
		self.resetTransactionFees();
	}

	resetTransactionFees() {
		const self = this;
		
		// Clear transaction fees from shopping cart
		if (self.storeService.shoppingCart) {
			self.storeService.shoppingCart.transactionFees = null;
		}
	}

	getRegisterCategories() {
		const self = this;

		self.dataService.getDataList<CustomRegisterCategory>(`/RegisterCategories/${self.registerId}`)
			.then(response => {
				self.toolsService.ProcessHttpResponse(response, () => {
					self.registerCategories = response.data;
					if (self.registerCategories && self.registerCategories.length) {
						self.getProductList(self.registerCategories[0]);
					}
				});
			});
	}

	getProductList(rc: CustomRegisterCategory) {
		const self = this;

		for (const register of self.registerCategories) {
			if (register.categoryId === rc.categoryId) {
				register.selected = true;
			} else {
				register.selected = false;
			}
		}

		const timezoneOffset = new Date((new Date).getTime()).getTimezoneOffset();
		self.dataService.getDataList<Product>(`/RegisterProductList?categoryId=${rc.categoryId}&registerId=${self.registerId}&timezoneOffset=${timezoneOffset}`)
			.then(response => {
				self.toolsService.ProcessHttpResponse(response, () => {
					const productList = response.data;
					productList.forEach(function (product) {
						if (product.isSessionProduct) {
							product.imageBtnIcons = ['fas', 'sliders-h'];
							product.isProductType = 'session';
						} else if (product.isModifier) {
							product.imageBtnIcons = ['fas', 'list'];
							product.isProductType = 'modifier';
						} else if (product.isIntegrateWithSkiData) {
							product.imageBtnIcons = ['fas', 'star'];
							product.isProductType = 'skidata';
						} else if (product.isArcadeType && (product.arcadeType === 14651 || product.arcadeType === 14652)) {
							product.imageBtnIcons = ['fas', 'gamepad'];
							product.isProductType = 'arcade';
						}
					});
					self.products = productList;
				});
			});
	}

	async addProduct(product: Product, quantity: number) {
		const self = this;

		const isCardUsedCheck = false;
		const currentDateTime: Date = null;
		const arcadeProd = 0;

		if (arcadeProd > 0) {
			quantity = 1;
		} else {
			quantity = quantity === 0 ? 1 : quantity;
		}
		if (!isCardUsedCheck && product.productId && product.productType) {
			if (product.isProductType === 'modifier' || product.isSessionProduct) {
				self.openModifierPopup(product, quantity);
			} else {
				const existingProduct = self.storeService.shoppingCart.products.find(x => (x.itemObjectId === product.productId) &&
					(x.productType === product.productType) && (x.isUndefinedPrice === product.isUndefinedPrice) && (x.price === product.unitPrice));

				if (existingProduct) {
					existingProduct.quantity += 1;
					self.toolsService.itemAdded(`${product.productName} added to your cart (${existingProduct.quantity})`);
				} else {
					const fullProduct = await self.getProduct(product.productId, product.productType, quantity,
						product.isUndefinedPrice, product.isNegativeUndefinedPrice, product.unitPrice, currentDateTime);

					if (fullProduct.itemObjectId > 0) {
						self.storeService.shoppingCart.products.push(fullProduct);
					}

					self.toolsService.itemAdded(`${product.productName} added to your cart`);
				}
			}
		}
	}

	async addProductFromPopup() {
		const self = this;
		if (self.sessions) {
			self.addSessionsProduct();
		}
		else {
			self.addProductWithModifiers();
		}
	}

	async addSessionsProduct() {
		const self = this;
		if (!self.selectedTimeslot) {
			self.toolsService.showError("Please select a time");
			return;
		}
		let avaialable = await self.validateSlotsAvailability();
		const quantity = self.qtyModifierSessions === 0 ? 1 : self.qtyModifierSessions;
		if (avaialable) {
			self.sessionsProductDTO.quantity = self.qtyModifierSessions;
			self.sessionsProductDTO.sessionname = self.sessionsProduct.productName;
			self.sessionsProductDTO.productImage = self.sessionsProduct.productImage;
			self.sessionsProductDTO.productPrice = self.sessionsProduct.productPrice;
			self.sessionsProductDTO.productSessionType = self.sessionsProduct.productSessionType;
			self.sessionsProductDTO.productSku = self.sessionsProduct.productSku;
			self.sessionsProductDTO.productStartTime = self.sessionsProduct.productStartTime;
			self.sessionsProductDTO.productType = self.sessionsProduct.productType;
			self.sessionsProductDTO.productTypeBasedId = self.sessionsProduct.productTypeBasedId;
			self.sessionsProductDTO.registerId = self.sessionsProduct.registerId;
			self.sessionsProductDTO.salesDateEnd = self.sessionsProduct.salesDateEnd;
			self.sessionsProductDTO.salesDateStart = self.sessionsProduct.salesDateStart;
			self.sessionsProductDTO.salesEndTime = self.sessionsProduct.salesEndTime;
			self.sessionsProductDTO.salesStartTime = self.sessionsProduct.salesStartTime;
			self.sessionsProductDTO.scheduleId = self.sessionsProduct.scheduleId;
			self.sessionsProductDTO.scheduleName = self.sessionsProduct.scheduleName;
			self.sessionsProductDTO.secondaryTicketTemplateId = self.sessionsProduct.secondaryTicketTemplateId;
			self.sessionsProductDTO.sellingAge = self.sessionsProduct.sellingAge;
			self.sessionsProductDTO.sessionGroupDetailId = self.sessionsProduct.sessionGroupDetailId;
			self.sessionsProductDTO.sessionGroupId = self.sessionsProduct.sessionGroupId;
			self.sessionsProductDTO.sessionGroupItems = self.sessionsProduct.sessionGroupItems;
			self.sessionsProductDTO.sessionGroupName = self.sessionsProduct.sessionGroupName;
			self.sessionsProductDTO.shelfLife = self.sessionsProduct.shelfLife;
			self.sessionsProductDTO.siteId = self.sessionsProduct.siteId;
			self.sessionsProductDTO.skiDataCategory = self.sessionsProduct.skiDataCategory;
			self.sessionsProductDTO.skiDataProductId = self.sessionsProduct.skiDataProductId;
			self.sessionsProductDTO.sortOrder = self.sessionsProduct.sortOrder;
			self.sessionsProductDTO.supplierAddress = self.sessionsProduct.supplierAddress;
			self.sessionsProductDTO.supplierEmail = self.sessionsProduct.supplierEmail;
			self.sessionsProductDTO.supplierId = self.sessionsProduct.supplierId;
			self.sessionsProductDTO.supplierName = self.sessionsProduct.supplierName;
			self.sessionsProductDTO.supplierPhone = self.sessionsProduct.supplierPhone;
			self.sessionsProductDTO.supplierZipCode = self.sessionsProduct.supplierZipCode;
			self.sessionsProductDTO.taxOptionId = self.sessionsProduct.taxOptionId;
			self.sessionsProductDTO.taxRate = self.sessionsProduct.taxRate;
			self.sessionsProductDTO.thermalId = self.sessionsProduct.thermalId;
			self.sessionsProductDTO.ticketTypeDescription = self.sessionsProduct.ticketTypeDescription;
			self.sessionsProductDTO.ticketTypeId = self.sessionsProduct.ticketTypeId;
			self.sessionsProductDTO.ticketTypeName = self.sessionsProduct.ticketTypeName;
			self.sessionsProductDTO.ticketable = self.sessionsProduct.ticketable;
			self.sessionsProductDTO.unitCost = self.sessionsProduct.unitCost;
			self.sessionsProductDTO.unitPerIssue = self.sessionsProduct.unitPerIssue;
			self.sessionsProductDTO.unitPrice = self.sessionsProduct.unitPrice;
			self.sessionsProductDTO.unitPriceWithCurrency = self.sessionsProduct.unitPriceWithCurrency;
			self.sessionsProductDTO.universalPricing = self.sessionsProduct.universalPricing;
			self.sessionsProductDTO.updatedByName = self.sessionsProduct.updatedByName;
			self.sessionsProductDTO.voucherPackageId = self.sessionsProduct.voucherPackageId;

			self.sessionsProductDTO.capacity = self.selectedTimeslot.slotCapacity;
			self.sessionsProductDTO.totalQuantity = self.qtyModifierSessions;
			self.sessionsProductDTO.startDateTime = self.getDateTime(self.selectedTimeslot.startDateTime)
			self.sessionsProductDTO.startDateTimeStr = self.selectedTimeslot.startDateTimeStr;
			self.sessionsProductDTO.endDateTime = self.getDateTime(self.selectedTimeslot.endTime);
			self.sessionsProductDTO.endDateTimeStr = self.selectedTimeslot.endDateTimeStr;
			self.sessionsProductDTO.duration = Math.floor(
				(Math.abs((new Date(self.selectedTimeslot.endTime).getTime() - new Date(self.selectedTimeslot.startDateTime).getTime()))
					/ 1000) / 60);
			self.sessionsProductDTO.productDetails.forEach(x => {
				x.quantity = self.qtyModifierSessions;
				x.productGroupQuantity = self.qtyModifierSessions;
				x.bookingStatus = 1;
				x.itemObjectId = self.sessionsProductDTO.productId;
			});
			self.addSessionsProductSP()
		}
		else {
			this.toolsService.showError('No Slots available at the moment. Please try later!')
		}
	}
	async addSessionsProductSP() {
		const self = this;
		//add line item for each quantity selected
		self.sessionsProductDTO.productDetails.forEach(detail => {
			detail.lineItems = [];
			for (let i = 0; i < detail.quantity; i++) {
				let lineItem = new LineItem();
				lineItem.lineItemId = 0;
				lineItem.sessionProductId = 0;
				lineItem.productDetaild = 0;
				lineItem.firstName = "";
				lineItem.lastName = "";
				lineItem.age = 0;
				lineItem.isWaiverRequired = true;
				lineItem.waiverId = 0;
				lineItem.isActive = true;
				lineItem.isCheckedIn = false;
				detail.lineItems.push(lineItem);
			}
		})
		const postData = {
			sessionProductModelStr: JSON.stringify(self.sessionsProductDTO),
			productType: self.sessionsProduct.productType,
			registerId: self.sessionsProduct.registerId,
			quantity: self.qtyModifierSessions,
			isUndefinedPrice: self.sessionsProduct.isUndefinedPrice,
			isNegativeUndefinedPrice: self.sessionsProduct.isNegativeUndefinedPrice,
			addTimerVal: (self.storeService.kioskSettings.timerAfterTransactionInSec),
			orderId: self.storeService.shoppingCart.orderId,
			updateTimer: 0
		};

		await self.dataService.post<ShoppingCart>(
			`/ShoppingCart/AddSessionProductSP`, postData, ShoppingCartProduct)
			.then(response => {
				return self.toolsService.ProcessHttpResponse(response, async () => {
					if (response.succeeded) {
						const product = self.sessionsProduct;
						const existingProduct = self.storeService.shoppingCart.products.find(x => (x.itemObjectId === product.productId) &&
							(x.productType === product.productType) && (x.isUndefinedPrice === product.isUndefinedPrice) &&
							(x.price === product.unitPrice && x.timeSlot && x.timeSlot.startDateTime == self.selectedTimeslot.startDateTime && x.timeSlot.endTime == self.selectedTimeslot.endTime));
						if (existingProduct) {
							existingProduct.quantity += this.qtyModifierSessions;
							self.toolsService.itemAdded(`${product.productName} added to your cart (${existingProduct.quantity})`);
						} else {
							let shoppingCart = response.data;
							let productList: ShoppingCartProduct[] = [];
							// shopping Cart would not have non sessioned products since they are not checked out already
							//so add them back after assigning cart products
							let nonShoppingCartProducts = self.storeService.shoppingCart.products.filter(p => p.isSessionProduct == false);
							self.storeService.shoppingCart.orderId = shoppingCart.orderId;
							localStorage.setItem("orderId", shoppingCart.orderId.toString())
							shoppingCart.products.forEach(p => {
								let shoppingCartProduct = new ShoppingCartProduct();
								shoppingCartProduct.allowedRenewals = p.allowedRenewals;
								shoppingCartProduct.arcadeProduct = p.arcadeProduct;
								shoppingCartProduct.arcadeProductName = p.arcadeProductName;
								shoppingCartProduct.billingFrequency = p.billingFrequency;
								shoppingCartProduct.categoryId = p.categoryId;
								shoppingCartProduct.ccToken = p.ccToken;
								shoppingCartProduct.customPrice = p.customPrice;
								shoppingCartProduct.discountAmount = p.discountAmount;
								shoppingCartProduct.endDateTime = p.endDateTime;
								shoppingCartProduct.finalPaymentDate = p.finalPaymentDate;
								shoppingCartProduct.firstPaymentDate = p.firstPaymentDate;
								shoppingCartProduct.guestAccountId = p.guestAccountId;
								shoppingCartProduct.guestAccountName = p.guestAccountName;
								shoppingCartProduct.guestId = p.guestId;
								shoppingCartProduct.guestName = p.guestName;
								shoppingCartProduct.initialPayment = p.initialPayment;
								shoppingCartProduct.isActive = p.isActive;
								shoppingCartProduct.isAllowPayment = p.isAllowPayment;
								shoppingCartProduct.isAllowPriceOverride = p.isAllowPriceOverride;
								shoppingCartProduct.isArcadeType = p.isArcadeType;
								shoppingCartProduct.isDeleted = p.isDeleted;
								shoppingCartProduct.isGiftCardEnabled = p.isGiftCardEnabled;
								shoppingCartProduct.isPayPlan = p.isPayPlan;
								shoppingCartProduct.isRenewalPackage = p.isRenewalPackage;
								shoppingCartProduct.isSessionProduct = p.isSessionProduct;
								shoppingCartProduct.isSkiDataProduct = p.isSkiDataProduct;
								shoppingCartProduct.isTaxExempted = p.isTaxExempted;
								shoppingCartProduct.isUndefinedPrice = p.isUndefinedPrice;
								shoppingCartProduct.itemObjectId = p.itemObjectId;
								shoppingCartProduct.itemObjectType = p.itemObjectType;
								shoppingCartProduct.membershipDiscountAmount = p.membershipDiscountAmount;
								shoppingCartProduct.membershipLength = p.membershipLength;
								shoppingCartProduct.membershipPackageExpiration = p.membershipPackageExpiration;
								shoppingCartProduct.membershipTypeId = p.membershipTypeId;
								shoppingCartProduct.modifierDetails = p.modifierDetails;
								shoppingCartProduct.modifierIds = p.modifierIds;
								shoppingCartProduct.nextPaymentDate = p.nextPaymentDate;
								shoppingCartProduct.parentPackageId = p.parentPackageId;
								shoppingCartProduct.payPlan = p.payPlan;
								shoppingCartProduct.payPlanBasePrice = p.payPlanBasePrice;
								shoppingCartProduct.payPlanTypeId = p.payPlanTypeId;
								shoppingCartProduct.price = p.price;
								shoppingCartProduct.priceAdjustment = p.priceAdjustment;
								shoppingCartProduct.priceWithoutAdjustment = p.priceWithoutAdjustment;
								shoppingCartProduct.productDetaild = p.productDetaild;
								shoppingCartProduct.productId = p.productId;
								shoppingCartProduct.productName = p.productName;
								shoppingCartProduct.productGuid = p.productGuid;
								shoppingCartProduct.productType = p.productType;
								shoppingCartProduct.quantity = p.quantity;
								shoppingCartProduct.quantityComped = p.quantityComped;
								shoppingCartProduct.recurringPeriods = p.recurringPeriods;
								shoppingCartProduct.salesPrice = p.salesPrice;
								shoppingCartProduct.selectedModifiers = p.selectedModifiers;
								shoppingCartProduct.sessionProductId = p.sessionProductId;
								shoppingCartProduct.shoppingCartProductId = p.shoppingCartProductId;
								shoppingCartProduct.productImage = p.productImage;
								shoppingCartProduct.startDate = p.startDate;
								shoppingCartProduct.startDateTime = p.startDateTime;
								shoppingCartProduct.taxAmount = p.taxAmount;
								shoppingCartProduct.taxOptionId = p.taxOptionId;
								shoppingCartProduct.taxRate = p.taxRate;
								shoppingCartProduct.timeDetails = p.timeDetails;
								shoppingCartProduct.timeSlot = this.selectedTimeslot;
								shoppingCartProduct.totalPrice = p.totalPrice;
								productList.push(shoppingCartProduct);
							})
							//add back non sessioned products
							nonShoppingCartProducts.forEach(p => {
								productList.push(p)
							})
							self.storeService.shoppingCart.products = productList;
							self.toolsService.itemAdded(`${product.productName} added to your cart`);
						}
						self.openModifier = false;
						self.qtyModifierSessions = 1;
					}
				});
			});
	}
	async addProductWithModifiers() {
		const self = this;

		const product = self.productWithModifiers;
		const quantity = self.qtyModifierSessions === 0 ? 1 : self.qtyModifierSessions;
		const modifierIds = self.getModifierIds(self.productModifiers);

		let isValid = true;

		self.productModifiers.forEach(pm => {
			if (pm.isRequireSelection && !pm.modifierDetails.some(x => x.isChecked)) {
				self.toolsService.showError(`Please select a value for ${pm.modifierName}.`);
				isValid = false;
			}
		});

		if (isValid) {
			const existingProduct = self.storeService.shoppingCart.products.find(x => (x.itemObjectId === product.productId) &&
				(x.productType === product.productType) && (x.isUndefinedPrice === product.isUndefinedPrice) &&
				(x.price === product.unitPrice) && (modifierIds === x.modifierIds));

			if (existingProduct) {
				existingProduct.quantity += 1;
				self.toolsService.itemAdded(`${product.productName} added to your cart (${existingProduct.quantity})`);
			} else {
				const fullProduct = await self.getProduct(product.productId, product.productType, quantity,
					product.isUndefinedPrice, product.isNegativeUndefinedPrice, product.unitPrice, null);

				fullProduct.selectedModifiers = [];
				self.productModifiers.forEach(pm => {
					pm.modifierDetails.forEach(md => {
						if (md.isChecked) {
							fullProduct.selectedModifiers.push({ ...md });
						}
					});
				});
				fullProduct.modifierIds = modifierIds;

				if (fullProduct.itemObjectId > 0) {
					self.storeService.shoppingCart.products.push(fullProduct);
				}
				self.toolsService.itemAdded(`${product.productName} added to your cart`);
			}

			// reset these variables
			self.productWithModifiers = null;
			self.productModifiers = [];
			self.qtyModifierSessions = 1;

			// close the dialog
			self.openModifier = false;
		}
	}


	getModifierIds(modifiers: ProductModifier[]): string {
		let modifierIds = '';

		if (modifiers && modifiers.length) {
			modifiers.forEach(modifier => {
				if (modifier.modifierDetails && modifier.modifierDetails.length) {
					modifier.modifierDetails.forEach(md => {
						if (md.isChecked) {
							if (modifierIds !== '') {
								modifierIds += ',';
							}
							modifierIds += md.modifierDetailId;
						}
					});
				}
			});
		}

		return modifierIds;
	}

	async getProduct(productId: number, productType: string, quantity: number = 0, isUndefinedPrice: boolean = false,
		isNegativeUndefinedPrice: boolean = false, customPrice: number = 0, currentDateTime: Date = null): Promise<ShoppingCartProduct> {
		const self = this;

		const postData = {
			shoppingCart: self.storeService.shoppingCart
		};

		const product = await self.dataService.post<ShoppingCartProduct>(
			`/ShoppingCart/GetProduct?productId=${productId}&productType=${productType}&registerId=${self.registerId}&quantity=${quantity}`, postData, ShoppingCartProduct)
			.then(response => {
				return self.toolsService.ProcessHttpResponse(response, () => {
					return response.data;
				});
			});
		product.price = customPrice;
		return product;
	}



	async validateSlotsAvailability(): Promise<boolean> {
		const self = this;
		let currentDate = new Date(self.selectedSessionProductDate);
		currentDate.setHours(0);
		currentDate.setMinutes(0);
		currentDate.setSeconds(0);
		console.log(currentDate.toString())
		const postData = {
			selectedDate: self.getDateTime(currentDate),
			productId: self.sessionsProduct.productId,
			productType: self.sessionsProduct.productType,
			slotQuantity: self.qtyModifierSessions,
			startDateTimeStr: self.getDateTime(self.selectedTimeslot.startDateTimeStr),
			endDateTimeStr: self.getDateTime(self.selectedTimeslot.endDateTimeStr)
		};
		const available = await self.dataService.post<any>(
			`/RegisterProductList/ValidateSlotAvailability`, postData)
			.then(response => {
				return self.toolsService.ProcessHttpResponse(response, () => {
					return response.succeeded;
				});
			});

		return available;
	}

	selectModifier(modifierType: string, modifierDetails: ModifierDetail[], modifier: ModifierDetail) {
		const self = this;

		const newValue = !modifier.isChecked;

		if (modifierType.toLowerCase() === 'single') {
			modifierDetails.forEach(md => md.isChecked = false);
		}

		modifier.isChecked = newValue;
	}

	clearCart() {
		const self = this;
		Utility.clearSessionCart(self.storeService, self.dataService, self.toolsService);
	}

	proceedToCheckout() {
		const self = this;

		const postData = {
			registerId: self.registerId,
			orderId: self.storeService.shoppingCart.orderId,
			browserOffset: tools.geBrowserTimeZoneandOffset(),
			products: self.storeService.shoppingCart.products
		};

		self.pageChange.emit(pages.ReviewOrder);
		window.scroll(0, 0)
	}

	backToCategories() {
		const self = this;

		self.products = [];
	}

	toggleCart() {
		const self = this;

		self.openCart ? self.openCart = false : self.openCart = true;
	}
	setSessionProductDateSelected(value) {
		const self = this;
		self.selectedSessionProductDate = value;
		self.selectedTimeslot = null;
		if (self.sessionsProduct) {
			self.loadTimeSlots(self.sessionsProduct);
		}
	}
	openModifierPopup(product: Product, quantity: number) {
		const self = this;

		self.productWithModifiers = product;
		self.openModifier = true;

		if (product.isProductType === 'modifier') {
			self.dataService.getDataList<ProductModifier>(
				`/ShoppingCart/GetProductModifiers?productId=${product.productId}&productType=${product.productType}` +
				`&quantity=${quantity}&isUndefinedPrice=${product.isUndefinedPrice}&customPrice=${product.unitPrice}`)
				.then(response => {
					self.toolsService.ProcessHttpResponse(response, () => {
						self.productModifiers = response.data;
					});
				});
			self.modifier = true;
			self.sessions = false;
		}
		if (product.isSessionProduct) {
			self.getSessionProduct(product);
			self.loadTimeSlots(product);
			self.sessionsProduct = product;
		}
	}
	loadTimeSlots(product: Product) {
		const self = this;
		self.selectedTimeslot = null;
		let sessionSelectedDateParam = "";
		if (self.selectedSessionProductDate) {
			sessionSelectedDateParam = `&selectedDate=${self.getDateTime(self.selectedSessionProductDate)}`;
		}
		self.dataService.getDataList<TimeSlots>(
			`/RegisterProductList/GetSessionProductTimeSlots?productId=${product.productId}&productType=${product.productType}${sessionSelectedDateParam}`)
			.then(response => {
				self.toolsService.ProcessHttpResponse(response, () => {
					self.timeSlots = response.data;
					self.sessions = true;
				});
			});
	}
	getSessionProduct(product: Product) {
		const self = this;
		self.dataService.get<SessionProduct>(
			`/RegisterProductList/ProductWorkFlowSession?productId=${product.productId}&productType=${product.productType}&registerId=${product.registerId}
			&isSessionProduct=true&productPrice=0&sessionProductId=0`)
			.then(response => {
				self.toolsService.ProcessHttpResponse(response, () => {
					if (response.succeeded) {
						self.sessionsProductDTO = response.data;
						self.dataService.get<any>(
							`/RegisterProductList/GetProductSessionRecord?productId=${product.productId}&productType=${product.productType}&currentDate=${self.getDateTime(self.selectedSessionProductDate)}&registerId=${product.registerId}&unitPrice=${product.unitPrice}`)
							.then(response => {
								if (response.succeeded) {
									//TODO : picking the first product details record for now. Need to pick the entire array later 
									self.sessionsProductDTO.productDetails = [response.data.productGroupRecord.find(x => x.productId == product.productId)];

								}
							});
					}
					else
						self.toolsService.showError(response.message);
				});
			});
	}
	selectTimeSlot(index: number, event: any) {
		const self = this;
		if (index < self.timeSlots.length) {
			for (var i = 0; i < self.timeSlots.length; i++) {
				self.timeSlots[i].selected = false;
			}
			self.timeSlots[index].selected = true;
			self.selectedTimeslot = self.timeSlots[index];
		}
		event.srcElement.checked = true;
	}
	changeQtyModifierSessions(qtyAdj: number, event) {
		const self = this;
		if (self.sessions) {
			//If no time slot is selected then don't let to change the quantity
			if (!self.selectedTimeslot) {
				self.toolsService.showError('Please select a timeslot before you can add quantity');
				event.preventDefault();
				return;
			}
			if ((self.qtyModifierSessions + qtyAdj) > self.selectedTimeslot.slotAvailable) {
				self.toolsService.showError('Total quantity cannot be greater than available slots');
				event.preventDefault();
				return;
			}

		}
		if (self.qtyModifierSessions > 1 || (qtyAdj > 0)) {
			self.qtyModifierSessions += qtyAdj;
		}
	}

	closeModifier() {
		const self = this;
		self.openModifier = false;
		self.modifier = false;
		self.sessions = false;
		self.qtyModifierSessions = 1
	}

	getDateTimeClientLocalDateTime(date) {
		//weekday: 'long',
		date = new Date(date);
		var options = { year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric' };
		return moment(date).lang("en-US").format('MMMM D, YYYY, h:mm:ss a');
		// → "Thursday, December 20, 2015"
	}

	getDateFormat_ddMMyyyyhhmmss(input) {
		var d = new Date(input);
		var y = d.getFullYear(), mon = (d.getMonth() + 1), dy = d.getDate(), h = d.getHours(), min = d.getMinutes(), ss = d.getSeconds();

		var dateformat = [('0' + mon.toString()).slice(-2), ('0' + dy.toString()).slice(-2), (y.toString())];
		var timeformat = [('0' + h.toString()).slice(-2), ('0' + min.toString()).slice(-2), ('0' + ss.toString()).slice(-2)];
		var dt = dateformat.join('/') + ' ' + timeformat.join(':');

		return dt;
	}

	getDateTime(date) {
		let self = this;
		let value = self.getDateTimeClientLocalDateTime(date);
		return this.getDateFormat_ddMMyyyyhhmmss(value);
	}

}
